// src/components/VaccinePreventionRequisitionList.js
import React, { useState, useEffect } from "react";
import {
  Box,
  VStack,
  HStack,
  Spinner,
  Text,
  Input,
  Heading,
  InputGroup,
  InputLeftElement,
} from "@chakra-ui/react";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileLines, faSearch } from "@fortawesome/free-solid-svg-icons";
import DataTable from "react-data-table-component";
import apiService from "../services/ApiService";
import { useAuth } from "../context/AuthContext";
import * as IconButton from "../themes/IconButtonSet";
import { StandardPageTitle, StandardHeader } from "../themes/StandardHeader";

// Custom styles for DataTable
const customStyles = {
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "10px",
    },
  },
  cells: {
    style: {
      padding: "10px",
      fontSize: "14px",
    },
  },
  rows: {
    style: {
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

const getBudgetTypeText = (type) => {
  switch (type) {
    case "Distribution":
      return "งบจำหน่าย";
    case "Prevention":
      return "งบป้องกัน";  
    default:
      return type || "";
  }
}

// ฟังก์ชันแปลงสถานะเป็นข้อความไทย
const getStatusText = (status) => {
  switch (status) {
    case "Draft":
      return "ร่างใบเบิก";
    case "Pending":
      return "รออนุมัติ";
    case "Approved":
      return "รอรับเข้าคลัง";
    case "Completed":
      return "รับเข้าคลังแล้ว";
    case "Cancelled":
      return "ยกเลิกใบเบิก";
    default:
      return status || "";
  }
};

// ฟังก์ชันกำหนดสไตล์สถานะ
const getStatusStyle = (status) => {
  switch (status) {
    case "Pending":
      return { bg: "#FFF3E0", color: "#D97706" };
    case "Confirmed":
      return { bg: "#D1FAE5", color: "#047857" };
    case "Completed":
      return { bg: "#E5E7EB", color: "#374151" };
    case "Cancelled":
      return { bg: "#FEE2E2", color: "#991B1B" };
    default:
      return { bg: "#EDF2F7", color: "#4A5568" };
  }
};

const VaccinePreventionRequisitionList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessionId } = useAuth();
  const [requisitions, setRequisitions] = useState([]);
  const [filteredRequisitions, setFilteredRequisitions] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    if (!sessionId) {
      navigate("/login", { state: { from: location.pathname } });
    }
  }, [sessionId, navigate, location.pathname]);

  const columns = [
    {
      name: "เลขที่เอกสาร",
      selector: (row) => row.TransactionID,
      sortable: true,
      width: "250px",
    },
    {
      name: "วันที่ขอเบิก",
      selector: (row) => new Date(row.RequestDate).toLocaleString("th-TH", {day: "numeric", month: "numeric", year: "numeric"}),
      sortable: true,
      width: "180px",
    },
    {
      name: "รอบการเบิก",
      selector: (row) => row.ReferenceDocumentNo,
      sortable: true,
      width: "180px",
    },
    {
      name: "ประเภทงบประมาณ",
      selector: (row) => getBudgetTypeText(row.BudgetType),
      sortable: true,
      width: "180px",
    },
    {
      name: "คลังวัคซีนที่ขอเบิก",
      selector: (row) => row.ToWarehouseName,
      sortable: true,
    },
    {
      name: "สถานะ",
      selector: (row) => (
        <Box
          bg={getStatusStyle(row.Status).bg}
          color={getStatusStyle(row.Status).color}
          borderColor={getStatusStyle(row.Status).color}
          borderWidth={1}
          borderRadius="full"
          px={3}
          py={1}
          textAlign="center"
          fontSize="sm"
          fontWeight="semibold"
          display="inline-block"
        >
          {getStatusText(row.Status)}
        </Box>
      ),
      sortable: true,
      width: "180px",
    },
    {
      name: "การดำเนินการ",
      cell: (row) => (
        <IconButton.View
          icon={faFileLines}
          onClick={() => navigate(`/vaccinepreventionrequisitionrequest/${row.TransactionID}`)}
          label="ดูรายละเอียด"
        />
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: "180px",
    },
  ];

  const fetchWarehouses = async () => {
    try {
      const response = await apiService({
        method: "POST",
        url: "/getVaccineWarehousePreventionsList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setWarehouses(response.VaccineWarehousesList || []);
      }
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const fetchRequisitions = async () => {
    setIsLoading(true);
    try {
      await fetchWarehouses();
      const response = await apiService({
        method: "POST",
        url: "/getVaccinePreventionRequisitionsRequestList",
        data: {},
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        const data = response.VaccineRequisitionsList || [];
        setRequisitions(data);
        setFilteredRequisitions(data);
      }
    } catch (error) {
      console.error("Error fetching requisitions:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      fetchRequisitions();
    }
  }, [sessionId]);

  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    setSearchTerm(value);

    if (!value) {
      setFilteredRequisitions(requisitions);
      return;
    }

    const searchTerms = value.split(" ").filter((term) => term.length > 0);
    const filtered = requisitions.filter((row) => {
      const fromWarehouseName =
        warehouses.find((w) => w.WarehouseID === row.FromWarehouseID)?.WarehouseName ||
        row.FromWarehouseID ||
        "";
      return searchTerms.every((term) =>
        [
          row.TransactionID.toLowerCase(),
          fromWarehouseName.toLowerCase(),
          row.BudgetType.toLowerCase(),
          row.ReferenceDocumentType?.toLowerCase() || "",
          row.ReferenceDocumentNo?.toLowerCase() || "",
          row.Status.toLowerCase(),
        ].some((field) => field.includes(term))
      );
    });
    setFilteredRequisitions(filtered);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setFilteredRequisitions(requisitions);
  };

  const handleCreateNew = () => {
    navigate("/vaccinepreventionrequisitionrequest/new");
  };

  if (!sessionId) {
    return null;
  }

  return (
    <Box
      p={{ base: 4, md: 10, lg: 35 }}
      m={{ base: 2, md: 10, lg: 25 }}
      borderWidth={1}
      borderRadius="lg"
      boxShadow="lg"
    >
      <VStack spacing={6} align="start">
        <StandardHeader
          title="รายการเบิกวัคซีน"
          bgColor="iBlue.600"
          icon={faFileLines}
        />
        <HStack
          w="100%"
          justify="space-between"
          flexWrap={{ base: "wrap", md: "nowrap" }}
        >
          {requisitions.length > 0 && (
            <InputGroup
              w={{ base: "100%", md: "50%", lg: "30%" }}
              mb={{ base: 4, md: 0 }}
            >
              <InputLeftElement pointerEvents="none">
                <FontAwesomeIcon icon={faSearch} color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="ค้นหาใบเบิกวัคซีน..."
                value={searchTerm}
                onChange={handleSearch}
                colorScheme="iBlue"
                mt={1}
              />
              <IconButton.Clear onClick={handleClearSearch} ml={5} />
            </InputGroup>
          )}
          {/*<IconButton.Add onClick={handleCreateNew} label="เพิ่มรายการใหม่" />*/}
        </HStack>
        {isLoading ? (
          <Spinner size="md" />
        ) : requisitions.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ยังไม่มีข้อมูลใบเบิกวัคซีน
          </Text>
        ) : filteredRequisitions.length === 0 ? (
          <Text fontSize="lg" color="gray.500" textAlign="center" w="100%">
            ไม่พบข้อมูลที่ค้นหา
          </Text>
        ) : (
          <Box w="100%" overflowX="auto">
            <DataTable
              columns={columns}
              data={filteredRequisitions}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={customStyles}
            />
          </Box>
        )}
      </VStack>
    </Box>
  );
};

export default VaccinePreventionRequisitionList;